/**
 * `X-Idempotency-Key` for requests that move money.
 *
 * The server runs such a request once per key and answers a repeat with the
 * first one's result. So the same key has to go out with every send of one
 * logical action: a second click before the first response, or a retry after
 * a response that never arrived.
 *
 * One key is kept per request (method, URL and body) while it is in flight
 * and for a few seconds after it settles; an identical request in that time
 * reuses it. After that the same request gets a fresh key, because recording
 * the same payment again a minute later is something a person may mean.
 *
 * A caller that manages its own key (the visit payment dialog pins one per
 * Save click) sets the header itself and is left alone.
 */
const MONEY_ROUTES: RegExp[] = [
  /^\/finances\/expenses\/[^/]+\/(pay|cancel)$/,
  /^\/finances\/expenses\/[^/]+\/payments\/[^/]+\/undo$/,
  /^\/finances\/invoices\/[^/]+\/(payment|refund)$/,
  /^\/finances\/wallet\/(smart-payment|advance-payment|use-balance|refund)$/,
  /^\/finances\/clinic-payments(\/[^/]+\/allocate)?$/,
  /^\/finances\/accounts\/(transfer|add-cash|withdraw)$/,
  /^\/finances\/reservations\/[^/]+\/refund$/,
  /^\/finances\/transactions$/,
  /^\/finances\/cash-counts$/,
  /^\/payroll\/periods\/payouts\/[^/]+\/pay$/,
  /^\/payroll\/payouts\/[^/]+\/mark-paid$/,
];

/** How long after a request settles an identical one still counts as a repeat. */
const REUSE_AFTER_SETTLE_MS = 3000;

interface Entry {
  key: string;
  inFlight: number;
  expiresAt: number;
}

const entries = new Map<string, Entry>();

function newKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function signature(method: string, path: string, body: unknown): string {
  let serialized = '';
  try {
    serialized = typeof body === 'string' ? body : JSON.stringify(body ?? null);
  } catch {
    // A body that cannot be serialized gets a key of its own every time.
    serialized = newKey();
  }
  return `${method.toUpperCase()} ${path} ${serialized}`;
}

export function isMoneyRoute(method: string | undefined, url: string): boolean {
  if (!method || method.toUpperCase() !== 'POST') return false;
  const path = url.split('?')[0];
  return MONEY_ROUTES.some((route) => route.test(path));
}

/** The key to send with this request, and the handle to release it with. */
export function claimIdempotencyKey(
  method: string,
  url: string,
  body: unknown,
): { key: string; signature: string } {
  const now = Date.now();
  for (const [sig, entry] of entries) {
    if (entry.inFlight === 0 && entry.expiresAt <= now) entries.delete(sig);
  }

  const sig = signature(method, url.split('?')[0], body);
  let entry = entries.get(sig);
  if (!entry) {
    entry = { key: newKey(), inFlight: 0, expiresAt: 0 };
    entries.set(sig, entry);
  }
  entry.inFlight += 1;
  return { key: entry.key, signature: sig };
}

/**
 * Call when the request settles. A success keeps the key briefly, so a late
 * duplicate is answered from the result. A failure drops it: the server kept
 * nothing, and the next attempt is a new request.
 */
export function releaseIdempotencyKey(sig: string, succeeded: boolean): void {
  const entry = entries.get(sig);
  if (!entry) return;
  entry.inFlight = Math.max(0, entry.inFlight - 1);
  if (entry.inFlight > 0) return;
  if (succeeded) entry.expiresAt = Date.now() + REUSE_AFTER_SETTLE_MS;
  else entries.delete(sig);
}
